import { Component, effect, inject } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ELicenseChannelState, ILicense } from '@web/dtos';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { SimpleTableComponent } from '../../../components/data-display/simple-table/simple-table.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { LicenseStore } from '../../../services/api/license/license-store.service';
import { ToggleComponent } from '../../../components/data-input/form-inputs/toggle/toggle.component';
import { map, Observable } from 'rxjs';
import { IDialogData } from '../../../components/dialogs/dialog';
import { ResetChangesDialogComponent } from '../../../components/dialogs/reset-changes-dialog/reset-changes-dialog.component';
import { DialogService } from '../../../services/support/dialog/dialog.service';
import { LicenseFormsFactoryService } from '../../../factory/license/license-forms-factory.service';
import {
  ILicenseForm,
  ILicenseFormWrapper,
} from '../../../entities/license-entity';
import { FormTracking } from '../form-tracking/form-tracking.component';

type LicenseChannel = 'webState' | 'appState';

@Component({
  selector: 'aw-license-page',
  imports: [
    ButtonComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    SimpleTableComponent,
    ToggleComponent,
  ],
  templateUrl: './license-page.component.html',
})
export class LicensePageComponent extends FormTracking<ILicenseFormWrapper> {
  protected readonly licenseStore: LicenseStore = inject(LicenseStore);
  private readonly licenseFormsFactory: LicenseFormsFactoryService = inject(
    LicenseFormsFactoryService
  );
  private readonly dialogService: DialogService = inject(DialogService);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly LicenseChannelState: typeof ELicenseChannelState =
    ELicenseChannelState;
  public licenseForm: FormGroup<ILicenseFormWrapper> =
    this.licenseFormsFactory.buildForm(this.licenseStore.licenses());

  constructor() {
    super();
    effect((): void => {
      const licenses: ILicense[] = [...this.licenseStore.licenses()].sort(
        (a: ILicense, b: ILicense): number => a.position - b.position
      );
      this.licenseForm = this.licenseFormsFactory.buildForm(licenses);
      this.initFormTracking(this.licenseForm);
    });
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.hasUnsavedChanges(this.licenseForm)) {
        event.preventDefault();
      }
    };
  }

  public handleAbortChangesDialog(): Observable<boolean> {
    const dialogRef = this.dialogService.openDialog<IDialogData>(
      ResetChangesDialogComponent,
      {
        headline: this.cmsTextProvider.shared.revertChanges,
        width: '600px',
        icon: 'warning',
        iconBackgroundColor: '#eed202',
      }
    );
    dialogRef.closed.subscribe(result => {
      if (result != undefined && result.submit) {
        if (result.acceptChanges) {
          this.save();
        } else {
          this.resetFormChanges();
        }
      }
    });
    return dialogRef.closed.pipe(map(result => result?.submit ?? false));
  }

  protected isChannelUnavailable(
    licenseForm: FormGroup<ILicenseForm>,
    channel: LicenseChannel
  ): boolean {
    return (
      licenseForm.controls[channel].value === ELicenseChannelState.Unavailable
    );
  }

  public async save(): Promise<boolean> {
    await this.licenseStore.save(
      this.licenseFormsFactory.buildEntity(this.licenseForm)
    );

    return true;
  }

  public resetFormChanges(): void {
    this.licenseStore.reload();
  }

  public navigateBack(): void {
    window.history.back();
  }
}
